import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useIntl } from "react-intl";
import { Checkbox, ClearableInput, Select, SimpleTooltip, Spinner } from "@rio-cloud/rio-uikit";
import { Asset, AssetGroup } from "../../models/asset";
import { compareAlphaFirstThenNumericNames } from "../../utils/stringUtils";

import UptimeTagRow from "./components/UptimeTagRow";

import useUptimeSidebar from "../../hooks/useUptimeSidebar";

import "./styles.scss";

interface UptimeSidebarProps {
	allTags: AssetGroup[];
	allAssets: Asset[];
	setAssetIds: (assetIds: string[]) => void;
	onUserInteraction?: () => void;
}

const LIMIT = 100;

const LOCALIZED_TAG_NAME_IDS: Record<string, string> = {
	"field-campains-group": "intl-msg:assetsTree.assetsTree.fieldCampaignGroup",
	"ungruped": "intl-msg:assetsTree.assetsTree.ungrouped",
};

const UptimeSidebar = ({
	allTags = [],
	allAssets = [],
	setAssetIds,
	onUserInteraction,
}: UptimeSidebarProps) => {
	const intl = useIntl();

	const {
		tagWithAssets,
		setTagWithAssets,
		getSearchLabel,
		viewMode,
		setViewMode,
		handleFilter,
		selectedTags,
		getVehicleTypeCount,
		searchQuery,
		addTag,
		removeTag,
		addAllTags,
		removeAllTags,
	} = useUptimeSidebar(allTags, allAssets);

	const [page, setPage] = useState(1);
	const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
	const hasUserExplicitlySelectedRef = useRef(false);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchQuery(searchQuery);
		}, 300);
		
		return () => clearTimeout(timer);
	}, [searchQuery]);
	
	const assetsMap = useMemo(() => {
		const map = new Map<string, Asset>();
		allAssets.forEach(asset => map.set(asset.id, asset));
		return map;
	}, [allAssets]);
	
	const lowerDebouncedQuery = useMemo(
		() => debouncedSearchQuery.toLowerCase(),
		[debouncedSearchQuery],
	);

	const getFilteredAssets = useCallback(
		(tagAssets: Asset[]) => {
			if (!debouncedSearchQuery || viewMode !== "assets") return tagAssets;
			return tagAssets.filter(
				(asset) =>
					asset.chassis?.toLowerCase().includes(lowerDebouncedQuery) ||
					asset.name?.toLowerCase().includes(lowerDebouncedQuery) ||
					asset.licensePlate?.toLowerCase().includes(lowerDebouncedQuery),
			);
		},
		[debouncedSearchQuery, viewMode, lowerDebouncedQuery],
	);
	
	useEffect(() => {
		if (!tagWithAssets) return;

		const allSelectedIds = Array.from(new Set(
			Object.values(tagWithAssets).flatMap(tag => Array.from(tag?.selectedAssetIds ?? new Set()))
		));

		if (allSelectedIds.length === 0) {
			// A árvore é a fonte da verdade: ao remontar (ex.: voltar de outra tela) ela começa vazia,
			// então o assetIds global também precisa ser limpo para não exibir dados sem seleção
			setAssetIds([]);
			return;
		}

		if (debouncedSearchQuery && viewMode === "assets") {
			const lowerQuery = debouncedSearchQuery.toLowerCase();
			const filteredIds = allSelectedIds.filter(assetId => {
				const asset = assetsMap.get(assetId);
				if (!asset) return false;
				return (
					asset.chassis?.toLowerCase().includes(lowerQuery) ||
					asset.name?.toLowerCase().includes(lowerQuery) ||
					asset.licensePlate?.toLowerCase().includes(lowerQuery)
				);
			});
			setAssetIds(filteredIds);
		} else {
			setAssetIds(allSelectedIds);
		}
	}, [tagWithAssets, debouncedSearchQuery, viewMode, assetsMap, setAssetIds]);

	// Reset page when search query changes
	useEffect(() => {
		setPage(1);
	}, [debouncedSearchQuery]);

	// Filter tags based on debounced search query — memoized to avoid recomputing on every render
	const localizedTags = useMemo(
		() =>
			allTags.map((tag: AssetGroup) =>
				LOCALIZED_TAG_NAME_IDS[tag.id]
					? { ...tag, name: intl.formatMessage({ id: LOCALIZED_TAG_NAME_IDS[tag.id] }) }
					: tag,
			),
		[allTags, intl],
	);

	const filteredTags = useMemo(
		() =>
			localizedTags.filter((tag: AssetGroup) => {
				if (
					debouncedSearchQuery &&
					viewMode === "tags" &&
					!tag.name?.toLowerCase().includes(lowerDebouncedQuery)
				)
					return false;
				return true;
			}),
		[localizedTags, debouncedSearchQuery, viewMode, lowerDebouncedQuery],
	);

	useEffect(() => {
		if (hasUserExplicitlySelectedRef.current || viewMode !== "tags" || !debouncedSearchQuery) return;

		addAllTags(undefined, filteredTags.map(tag => tag.id));
	}, [debouncedSearchQuery, viewMode, filteredTags]);

	// Apply pagination only if no search query is active
	const paginatedTags = useMemo(
		() => (debouncedSearchQuery ? filteredTags : filteredTags.slice(0, page * LIMIT)),
		[debouncedSearchQuery, filteredTags, page],
	);

	const getFilteredAssetIdsMap = useCallback(() => {
		const map: { [tagId: string]: string[] } = {};
		paginatedTags.forEach((tag) => {
			const tagAssets = tagWithAssets?.[tag.id]?.assets ?? [];
			const filtered = getFilteredAssets(tagAssets);
			if (filtered.length > 0) {
				map[tag.id] = filtered.map((asset) => asset.id);
			}
		});
		return map;
	}, [paginatedTags, tagWithAssets, getFilteredAssets]);

	if (!allAssets?.length || !allTags?.length) {
		return (
			<div className="height-100pct display-flex align-items-center justify-content-center">
				<Spinner />
			</div>
		);
	}

	return (
		<div className="Tree" style={{ height: "100%" }}>
			<div className="TreeHeader">
				<div className="TreeSearch width-100pct">
					<div className="input-group width-100pct">
						<div className="input-group width-100pct">
							<span className="input-group-addon">
								<span className="rioglyph rioglyph-search" aria-hidden="true" />
							</span>{" "}
							<ClearableInput placeholder={getSearchLabel(viewMode)} onChange={handleFilter} />
							<SimpleTooltip content={intl.formatMessage({ id: "intl-msg:assetsTree.uptimeSidebar.searchType" })}>
								<span className="input-group-btn">
									<Select
										pullRight={true}
										showSelectedItemIcon={true}
										dropdownClassName="width-auto"
										onChange={(item) => setViewMode(item?.short!)}
										options={[
											{
												id: "1",
												label: intl.formatMessage({ id: "intl-msg:assetsTree.uptimeSidebar.vehicles" }),
												short: "assets",
												icon: <span className="rioglyph text-size-16 rioglyph-status-driving text-color-primary" />,
												selected: viewMode == "assets",
											},
											{
												id: "2",
												label: intl.formatMessage({ id: "intl-msg:assetsTree.uptimeSidebar.groups" }),
												short: "tags",
												icon: <span className="rioglyph text-size-16 rioglyph-tag text-color-primary" />,
												selected: viewMode == "tags",
											},
										]}
										placeholder={intl.formatMessage({ id: "intl-msg:assetsTree.uptimeSidebar.searchType" })}
									/>
								</span>
							</SimpleTooltip>
						</div>
					</div>
				</div>

				<div className="TreeHead display-flex gap-5 padding-15">
					<div className="border border-right-only hidden-empty padding-right-10 margin-right-2">
						<div className="TreeSelectAll display-flex align-items-center">
							<label className="checkbox margin-top--1" id="checkbox-all">
								<Checkbox
									indeterminate={selectedTags.size > 0 && selectedTags.size < allTags.filter(el => el.id).length}
									checked={selectedTags.size === allTags.filter(el => el.id).length}
									onChange={() => {
										hasUserExplicitlySelectedRef.current = true;
										onUserInteraction?.();
										if (selectedTags.size < allTags.filter(el => el.id).length) {
											if (searchQuery && viewMode === "assets") {
												addAllTags(getFilteredAssetIdsMap());
											} else if (searchQuery && viewMode === "tags") {
												addAllTags(undefined, filteredTags.map(tag => tag.id));
											} else {
												addAllTags();
											}
										} else {
											removeAllTags();
										}
									}}
								/>
							</label>
						</div>
					</div>
					<div className="display-flex justify-content-between align-items-start width-100pct">
						<div className="TreeSummary display-flex flex-wrap align-items-center gap-10 padding-left-3">
							{getVehicleTypeCount("truck") >= 1 && (
								<div className="TypeCounter display-flex align-items-center user-select-none margin-right-2 cursor-pointer">
									<span className="rioglyph rioglyph-truck text-size-16 margin-right-2"></span>
									<span className="TreeLabelCount label label-condensed label-muted label-filled">{getVehicleTypeCount("truck")}</span>
								</div>
							)}
							{getVehicleTypeCount("bus") >= 1 && (
								<div className="TypeCounter display-flex align-items-center user-select-none margin-right-2 cursor-pointer">
									<span className="rioglyph rioglyph-bus text-size-16 margin-right-2"></span>
									<span className="TreeLabelCount label label-condensed label-muted label-filled">{getVehicleTypeCount("bus")}</span>
								</div>
							)}
							{getVehicleTypeCount("van") >= 1 && (
								<div className="TypeCounter display-flex align-items-center user-select-none margin-right-2 cursor-pointer">
									<span className="rioglyph rioglyph-van text-size-16 margin-right-2"></span>
									<span className="TreeLabelCount label label-condensed label-muted label-filled">{getVehicleTypeCount("van")}</span>
								</div>
							)}
							{getVehicleTypeCount("trailer") >= 1 && (
								<div className="TypeCounter display-flex align-items-center user-select-none margin-right-2 cursor-pointer">
									<span className="rioglyph rioglyph-trailer text-size-16 margin-right-2"></span>
									<span className="TreeLabelCount label label-condensed label-muted label-filled">{getVehicleTypeCount("trailer")}</span>
								</div>
							)}
							{getVehicleTypeCount("car") >= 1 && (
								<div className="TypeCounter display-flex align-items-center user-select-none margin-right-2 cursor-pointer">
									<span className="rioglyph rioglyph-car text-size-16 margin-right-2"></span>
									<span className="TreeLabelCount label label-condensed label-muted label-filled">{getVehicleTypeCount("car")}</span>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			<div
				className="TreeRoot user-select-none"
				style={{height: `calc(100% - 100px)`, overflow: "auto"}}
			>
					{paginatedTags
						.map((tag) => {
							const tagAssets = tagWithAssets?.[tag.id] ?? { assets: [], selectedAssetIds: new Set() };
							if (tagAssets?.assets?.length === 0) return null;

							const filteredAssets = getFilteredAssets(tagAssets.assets);
							const filteredAssetIds = filteredAssets.map(asset => asset.id);

							return <UptimeTagRow
								key={tag.id}
								tagId={tag.id}
								tagName={tag.name}
								isChecked={selectedTags.has(tag.id)}
								assets={tagAssets.assets}
								selectedAssetIds={tagAssets.selectedAssetIds}
								onTagSelect={() => {
									hasUserExplicitlySelectedRef.current = true;
									onUserInteraction?.();
									const allTagsSelected = selectedTags.size > 0 && selectedTags.size === allTags.filter(el => el.id).length;

									if (allTagsSelected) {
										removeAllTags();
										if (debouncedSearchQuery && viewMode === "assets") {
											addTag(tag.id, filteredAssetIds);
										} else {
											addTag(tag.id);
										}
									} else if (selectedTags.has(tag.id)) {
										removeTag(tag.id);
									} else {
										if (debouncedSearchQuery && viewMode === "assets") {
											addTag(tag.id, filteredAssetIds);
										} else {
											addTag(tag.id);
										}
									}
								}}
								isAssetChecked={(assetId: string) => tagWithAssets?.[tag.id]?.selectedAssetIds.has(assetId) ?? false}
								onAssetSelect={(assetId: string) => {
									hasUserExplicitlySelectedRef.current = true;
									onUserInteraction?.();
									setTagWithAssets(prev => {
										const currentSelectedIds = prev![tag.id]?.selectedAssetIds ?? new Set<string>();
										const newSelectedIds = new Set(currentSelectedIds);
										
										if (newSelectedIds.has(assetId)) {
											newSelectedIds.delete(assetId);
										} else {
											newSelectedIds.add(assetId);
										}
										
										return {
											...prev,
											[tag.id]: {
												...prev![tag.id],
												selectedAssetIds: newSelectedIds
											}
										};
									});
								}}
								searchQuery={debouncedSearchQuery}
								viewMode={viewMode}
							/>
						})}

					{/* Show "Carregar mais" button only when no search query is active and there are more tags to load */}
				{!debouncedSearchQuery && paginatedTags.length < filteredTags.length && (
						<div
							className="LoadMore cursor-pointer text-center py-8 text-color-primary"
							onClick={() => setPage(prev => prev + 1)}
						>
							{intl.formatMessage({ id: "intl-msg:assetsTree.uptimeSidebar.loadMore" })}
						</div>
					)}
			</div>
		</div>
	);
};

export default UptimeSidebar;
